const WRAPPERS: Record<string, string> = {
  '"': '"', "'": "'", "`": "`", "“": "”", "‘": "’", "«": "»", "<": ">", "(": ")", "[": "]",
};
const CLOSERS = new Map(Object.entries(WRAPPERS).map(([open, close]) => [close, open]));
const BARE_DOMAIN = /^[a-z\d]([a-z\d-]*[a-z\d])?(\.[a-z\d-]+)*\.[a-z]{2,}(?::\d+)?(?:[/?#]\S*)?$/i;

/**
 * A link the way people paste it: in quotes or brackets, or at the end of a
 * sentence. Returns the link without those, or the trimmed text unchanged
 * when there is nothing to take off.
 */
export function unwrapLink(value: string): string {
  let link = value.trim();
  for (;;) {
    const first = link[0];
    const last = link[link.length - 1];
    if (first && WRAPPERS[first]) {
      link = (link.length > 1 && last === WRAPPERS[first] ? link.slice(1, -1) : link.slice(1)).trim();
    } else if (/[.,;:!?]/.test(last ?? "")) {
      link = link.slice(0, -1).trim();
    } else if (last && CLOSERS.has(last) && !link.includes(CLOSERS.get(last)!)) {
      // A closing bracket the link never opened, e.g. "(see example.com/a)".
      // Wikipedia-style links keep theirs: example.com/Foo_(bar).
      link = link.slice(0, -1).trim();
    } else {
      return link;
    }
  }
}

/** One link and nothing else, once quotes, brackets and end punctuation are off. */
export function isLink(value: string): boolean {
  const link = unwrapLink(value);
  return !/\s/.test(link) && (/^https?:\/\/\S+$/i.test(link) || BARE_DOMAIN.test(link));
}

/**
 * People paste bare domains ("example.com/post") and wrapped links
 * ("“https://example.com/post”"). Give back the link itself with https:// so
 * it reads like the same link typed into Generate; anything else is returned
 * trimmed and unchanged, so the source check can still explain what's wrong.
 */
export function withLinkScheme(value: string): string {
  if (!isLink(value)) return value.trim();
  const link = unwrapLink(value);
  return /^https?:\/\//i.test(link) ? link : `https://${link}`;
}

/** Longest leftover text still read as instructions for the links; longer text is content. */
export const MAX_INSTRUCTION_CHARS = 300;

/**
 * Links typed with a request around them, as if the box were a prompt:
 * `"https://example.com/post" make it about hiring`. Returns the links and
 * the rest as instructions, or null when there is no full link (http(s):// or
 * www.) or the rest is long enough to be content in its own right.
 */
export function splitLinksFromInstructions(text: string): { links: string[]; instructions: string } | null {
  const links: string[] = [];
  const rest = text.replace(/\S*?(?:https?:\/\/|\bwww\.)\S+/gi, (token) => {
    if (!isLink(token)) return token;
    links.push(withLinkScheme(token));
    return " ";
  });
  if (!links.length) return null;
  const instructions = rest
    .replace(/\s+/g, " ")
    .replace(/^[\s"'`“”‘’«»:;,.\-–—]+|[\s"'`“”‘’«»:;,\-–—]+$/g, "")
    .trim();
  if (instructions.length > MAX_INSTRUCTION_CHARS) return null;
  return { links, instructions };
}

/**
 * Text handed over that is really one link with a short request around it
 * ("https://shop.com make me a carousel about my product"): the link to read
 * and the request as instructions. Null for anything else.
 */
export function linkInText(text: string): { link: string; instructions: string } | null {
  const split = splitLinksFromInstructions(text);
  return split?.links.length === 1 && split.instructions
    ? { link: split.links[0], instructions: split.instructions }
    : null;
}

/**
 * What a link handed over from the website or onboarding holds: usually one
 * link, sometimes a link with a request typed around it.
 */
export function incomingLink(value: string): { link: string; instructions: string } {
  const split = isLink(value) ? null : splitLinksFromInstructions(value);
  return split?.links.length === 1
    ? { link: split.links[0], instructions: split.instructions }
    : { link: withLinkScheme(value), instructions: "" };
}

export type SourceInputShape = {
  shape: "links" | "links_with_instructions" | "text";
  link_count: number;
  wrapped_link_count: number;
  instruction_chars: number;
  instructions_position: "before" | "after" | "around" | null;
  text_chars: number;
};

/**
 * How the Generate box will read what was typed, for analytics. Never
 * returns the text itself: only its shape and counts.
 */
export function describeSourceInput(text: string): SourceInputShape {
  const value = text.trim();
  const lines = value.split("\n").map((line) => line.trim()).filter(Boolean);
  const tokens = value.match(/\S*?(?:https?:\/\/|\bwww\.)\S+/gi) ?? [];
  const wrapped = (items: string[]) => items.filter((item) => isLink(item) && unwrapLink(item) !== item.trim()).length;
  if (lines.length && lines.every(isLink))
    return { shape: "links", link_count: lines.length, wrapped_link_count: wrapped(lines), instruction_chars: 0, instructions_position: null, text_chars: value.length };
  const split = splitLinksFromInstructions(value);
  if (!split) return { shape: "text", link_count: tokens.filter(isLink).length, wrapped_link_count: 0, instruction_chars: 0, instructions_position: null, text_chars: value.length };
  const linkTokens = tokens.filter(isLink);
  const first = value.indexOf(linkTokens[0]);
  const lastToken = linkTokens[linkTokens.length - 1];
  const before = value.slice(0, first).trim().length > 0;
  const after = value.slice(value.lastIndexOf(lastToken) + lastToken.length).trim().length > 0;
  return {
    shape: split.instructions ? "links_with_instructions" : "links",
    link_count: split.links.length,
    wrapped_link_count: wrapped(linkTokens),
    instruction_chars: split.instructions.length,
    instructions_position: !split.instructions ? null : before && after ? "around" : before ? "before" : "after",
    text_chars: value.length,
  };
}
